/**
 * §2.8 — a catalogue of messages, and the reader who is looking at one.
 *
 * Here rather than in `portal-kit` because two very different callers need the
 * same lookup. A console asks for the language *this tab* reads in; a mailer
 * asks for the language *the recipient* reads in, on a server that has no tab
 * and may be composing for a hundred people in one run. The words and the
 * fallback chain are identical either way — what differs is who is asking, and
 * that is the argument.
 *
 * Nothing here touches a DOM, a browser global or module state. `portal-kit`
 * layers the tab's own current locale on top (`tabTranslator`), which is the
 * part a server must never have.
 */
import { LOCALE_TAG } from "./locales.js";
/**
 * §2.8d — the order a message is looked for in.
 *
 * requested → its base language → operator default → its base → `en`.
 *
 * The base-language step is the one that earns its keep. Somebody whose
 * browser says `pt-PT` and a catalogue written in `pt-BR` are the same
 * language to a reader and different strings to a `Map`; without this step
 * that reader gets English while a complete Portuguese translation sits one
 * key away. It runs in one direction only — `pt` requested finds nothing under
 * `pt`, and `pt-BR` is *not* reached, because narrowing a language to a region
 * nobody asked for is a guess about currency, date order and idiom, and the
 * one place it goes wrong is a legal notice.
 */
export function localeChain(requested, operatorDefault) {
    const out = [];
    const add = (tag) => {
        if (!tag || !LOCALE_TAG.test(tag))
            return;
        if (!out.includes(tag))
            out.push(tag);
        const base = tag.slice(0, 2);
        if (base !== tag && !out.includes(base))
            out.push(base);
    };
    add(requested);
    add(operatorDefault);
    if (!out.includes('en'))
        out.push('en');
    return out;
}
/**
 * Reads a catalogue in one language, falling back through `localeChain`.
 *
 * The key is typed `keyof C`, so a message id that does not exist is a compile
 * error rather than the string `nav.dashboard` appearing on a statement. That
 * is deliberate division of labour with §2.8d: the *chain* handles a missing
 * translation and the *type* handles a missing message, because those are
 * different mistakes with different fixes and one runtime fallback would hide
 * the second behind the first.
 */
export function translator(catalogue, requested, operatorDefault) {
    const chain = localeChain(requested, operatorDefault);
    const t = (key, params) => {
        const message = catalogue[key];
        // Unreachable through the type. If a JS caller gets here, the id is the
        // only thing we know, and it is still better than an empty line on an
        // invoice — but nothing routes here on purpose.
        if (!message)
            return key;
        let text = '';
        for (const tag of chain) {
            const candidate = message[tag];
            // An empty string is a missing translation, not a translation into
            // nothing. §2.8d names the blank explicitly as the thing not to show.
            if (candidate !== undefined && candidate !== '') {
                text = candidate;
                break;
            }
        }
        if (text === '')
            text = message.en;
        return params ? interpolate(text, params) : text;
    };
    return Object.assign(t, { locale: chain[0] ?? 'en', chain });
}
/**
 * `{name}` → the parameter. An unknown placeholder is left standing rather
 * than blanked: `Remove {affiliate}?` on screen is a bug report, and
 * `Remove ?` is a mystery.
 */
function interpolate(text, params) {
    return text.replace(/\{(\w+)\}/g, (whole, name) => {
        const value = params[name];
        return value === undefined ? whole : String(value);
    });
}
/**
 * Message ids that have no translation in `locale` and would fall back.
 *
 * The point is a test: a language we *ship* should be complete, so §2.8d is
 * the safety net rather than the mechanism. Falling back is correct behaviour
 * and a shipped language relying on it is a gap in the translation, which is
 * the sort of thing that is obvious in a list and invisible on a screen.
 */
export function untranslated(catalogue, locale) {
    const chain = localeChain(locale).filter((tag) => tag !== 'en');
    return Object.keys(catalogue)
        .filter((key) => {
        const message = catalogue[key];
        if (!message)
            return true;
        return !chain.some((tag) => {
            const candidate = message[tag];
            return candidate !== undefined && candidate !== '';
        });
    })
        .sort();
}
/**
 * §2.8b — does this read as an identifier rather than as words?
 *
 * `ATTRIBUTED`, `CPA_HOLD`, `FTD_VALUE`: the shape of a status or a reason
 * code. A catalogue entry with a value like this means somebody has put an
 * identifier behind a translation lookup, and the Spanish column of that row
 * is a status value that no export, no filter and no support conversation
 * will recognise. Used by the tests that guard the shipped catalogues.
 */
export function identifierLike(text) {
    return /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(text) && text.length > 2;
}
//# sourceMappingURL=catalogue.js.map